import { useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { admin } from '@repletoryreach/sdk-api';
import { Stack, Skeleton, Box, Button, Text, Group } from '@repletoryreach/sdk-ui';
import { Alert, Tabs, BreadcrumbTrail, PageHeader } from '@repletoryreach/sdk-ui/data-display';
import { RunHeader } from './components/RunHeader';
import { OverviewTab } from './components/OverviewTab';
import { TraceTab } from './components/TraceTab';
import { RelatedRunsTab } from './components/RelatedRunsTab';
import { RawTab } from './components/RawTab';
import { formatDateTime } from '../../../lib/format';
import { useT } from '../../../i18n/I18nProvider';

// 30-day window for "find by id" + sibling discovery. Admin audit endpoints
// only expose listing, so a single-run GET goes through the same list with a
// client-side find. Acceptable because the list is already cached for /flows.
const SINCE_DAYS = 30;

// A run still in flight is polled, because both the live badge and the elapsed
// time are computed at render and would otherwise sit frozen on the screen.
const LIVE_POLL_MS = 15_000;

const TABS = ['overview', 'trace', 'related', 'raw'] as const;
type TabKey = (typeof TABS)[number];

export default function FlowViewPage() {
  const t = useT();
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const since = useMemo(() => new Date(Date.now() - SINCE_DAYS * 86_400_000).toISOString(), []);
  const { data, isLoading, isFetching, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['admin', 'audit', 'flow-runs', since],
    queryFn: () => admin.listFlowRuns(since),
    refetchInterval: (query) => {
      const current = query.state.data?.data?.find((r) => r.id === id);
      return current && !current.finished_at ? LIVE_POLL_MS : false;
    }
  });

  const runs = useMemo(() => data?.data ?? [], [data]);
  // The moment this page's answer was true. A polled page that never says so
  // leaves an operator reading a minute-old duration as the present.
  const dataAsOf = useMemo(() => new Date(dataUpdatedAt).toISOString(), [dataUpdatedAt]);
  const run = useMemo(() => runs.find((r) => r.id === id), [runs, id]);

  const requested = params.get('tab');
  const tab: TabKey = TABS.includes(requested as TabKey) ? (requested as TabKey) : 'overview';
  // The tab lives in the URL so a link to a run's trace can be pasted straight
  // into an incident thread.
  function selectTab(next: string) {
    const merged = new URLSearchParams(params);
    merged.set('tab', next);
    setParams(merged, { replace: true });
  }

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={140} />
        <Skeleton height={260} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert color="error" title={t('flows.view.loadErrorTitle')}>
        <Stack gap="sm" align="flex-start">
          <Text role="bodySmall">{(error as Error).message}</Text>
          <Button size="xs" variant="outline" loading={isFetching} onClick={() => void refetch()}>{t('action.retry')}</Button>
        </Stack>
      </Alert>
    );
  }

  if (!run) {
    return (
      <Alert color="warning" title={t('common.notFound')}>
        <Stack gap="sm" align="flex-start">
          <Text role="bodySmall">{t('flows.view.notFound', { days: SINCE_DAYS })}</Text>
          <Button size="xs" variant="outline" onClick={() => navigate('/flows')}>{t('flows.view.run.allRuns')}</Button>
        </Stack>
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      <BreadcrumbTrail
        items={[{ label: t('flows.title'), href: '/flows' }, { label: run.flow_name ?? t('flows.flowDeleted') }]}
        renderLink={(href, label) => <Link to={href}>{label}</Link>}
      />
      <PageHeader
        eyebrow={t('flows.view.eyebrow')}
        // The flow's name titles the page. The run's id is what a person
        // quotes, not what they read, so it lives in the summary strip behind
        // a copy button.
        title={run.flow_name ?? t('flows.flowDeleted')}
        description={t('flows.view.dataAsOf', { time: formatDateTime(dataAsOf) })}
        actions={
          <Group gap="sm" wrap="wrap">
            <Button size="xs" variant="outline" loading={isFetching} onClick={() => void refetch()}>{t('action.refresh')}</Button>
            <Button size="xs" variant="subtle" onClick={() => navigate(`/tenants/${run.organisation_id}`)}>{t('flows.view.run.openTenant')}</Button>
            <Button size="xs" variant="subtle" onClick={() => navigate('/flows')}>{t('flows.view.run.allRuns')}</Button>
          </Group>
        }
      />
      <RunHeader run={run} />
      <Tabs value={tab} onChange={selectTab}>
        <Tabs.List>
          <Tabs.Tab value="overview">{t('common.overview')}</Tabs.Tab>
          <Tabs.Tab value="trace">{t('flows.view.tabs.trace')}</Tabs.Tab>
          <Tabs.Tab value="related">{t('flows.view.tabs.related')}</Tabs.Tab>
          <Tabs.Tab value="raw">{t('flows.view.tabs.raw')}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="overview">
          <Box py="md"><OverviewTab run={run} /></Box>
        </Tabs.Panel>
        <Tabs.Panel value="trace">
          <Box py="md"><TraceTab run={run} /></Box>
        </Tabs.Panel>
        <Tabs.Panel value="related">
          <Box py="md"><RelatedRunsTab run={run} allRuns={runs} sinceDays={SINCE_DAYS} /></Box>
        </Tabs.Panel>
        <Tabs.Panel value="raw">
          <Box py="md"><RawTab run={run} /></Box>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
